import { useEffect, useState } from "react";
import { scenarios, scenarioMap } from "../domain/scenarios";
import { ScenarioSelector } from "../components/ScenarioSelector/ScenarioSelector";
import { InputEditor } from "../components/InputEditor/InputEditor";
import { ToolCallCard } from "../components/ToolCallCard/ToolCallCard";
import { ToolResultPanel } from "../components/ToolResultPanel/ToolResultPanel";
import { useToolCallRun } from "../hooks/useToolCallRun";
import "./Workbench.css";

const DEFAULT_SCENARIO_ID = scenarios[0]!.id;

function fixturePayloadText(scenarioId: string): string {
  const scenario = scenarioMap[scenarioId];
  return JSON.stringify(scenario?.fixture.payload ?? {}, null, 2);
}

export function Workbench() {
  const [selectedId, setSelectedId] = useState(DEFAULT_SCENARIO_ID);
  const scenario = scenarioMap[selectedId] ?? scenarios[0]!;
  const [inputText, setInputText] = useState(() => fixturePayloadText(selectedId));
  const [parseError, setParseError] = useState<string | undefined>(undefined);

  const run = useToolCallRun(scenario);

  useEffect(() => {
    setInputText(fixturePayloadText(selectedId));
    setParseError(undefined);
  }, [selectedId]);

  function handleRun(): void {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(inputText) as Record<string, unknown>;
    } catch (error) {
      setParseError(
        `El payload no es JSON válido: ${error instanceof Error ? error.message : "error desconocido"}.`,
      );
      return;
    }
    setParseError(undefined);
    run.request({
      schemaVersion: "1.0.0",
      scenarioId: scenario.id,
      payload: parsed,
      options: { deterministic: true },
    });
  }

  function handleResetToFixture(): void {
    setInputText(fixturePayloadText(selectedId));
    setParseError(undefined);
  }

  function handleReset(): void {
    // Solo limpia el estado de la máquina de ejecución; conserva la edición del
    // visitante en el payload para que "cambiar un parámetro y volver a ejecutar"
    // (03-ux-flujos-y-contenido.md) no descarte lo que acaba de escribir.
    run.reset();
  }

  return (
    <section id="workbench" className="workbench" aria-labelledby="workbench-heading">
      <h2 id="workbench-heading">Workbench</h2>
      <ScenarioSelector
        scenarios={scenarios}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />
      <div className="workbench__grid">
        <div className="workbench__column">
          <InputEditor
            value={inputText}
            onChange={setInputText}
            onResetToFixture={handleResetToFixture}
            parseError={parseError}
            disabled={run.state.status === "running"}
          />
          <ToolCallCard
            scenario={scenario}
            state={run.state}
            onRequest={handleRun}
            onGrantPermission={run.grantPermission}
            onDenyPermission={run.denyPermission}
            onConfirm={run.confirm}
            onCancelConfirmation={run.cancelConfirmation}
            onCancelRun={run.cancelRun}
            onReset={handleReset}
          />
        </div>
        <div className="workbench__column">
          <h3 id="result-heading">Resultado</h3>
          <div aria-labelledby="result-heading" data-testid="result-panel">
            <ToolResultPanel state={run.state} onRetry={handleReset} />
          </div>
        </div>
      </div>
    </section>
  );
}
